import type { TypedSupabaseClient } from '../client';
import {
  generateOccurrencesForRule,
  type DueRule,
} from '@erp/domain/payments';
import type {
  IRecurringPaymentRepository,
  RecurringPayment,
  CreateRecurringPaymentInput,
  UpdateRecurringPaymentInput,
  RecurringPaymentFilters,
  GenerateDueResult,
  PaymentType,
} from '../../../interfaces/src';

const PAYMENT_TYPE_PREFIX: Record<PaymentType, string> = {
  receipt: 'ENC',
  disbursement: 'DEC',
};

const PAYMENT_TYPE_SEQUENCE: Record<PaymentType, string> = {
  receipt: 'seq_payment_receipt',
  disbursement: 'seq_payment_disbursement',
};

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export class SupabaseRecurringPaymentRepository implements IRecurringPaymentRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: RecurringPaymentFilters): Promise<RecurringPayment[]> {
    let query = this.supabase.from('recurring_payments').select('*');
    if (filters?.isActive !== undefined) query = query.eq('is_active', filters.isActive);
    if (filters?.paymentType) query = query.eq('payment_type', filters.paymentType);
    if (filters?.thirdPartyType) query = query.eq('third_party_type', filters.thirdPartyType);
    if (filters?.search) {
      query = query.ilike('label', `%${filters.search}%`);
    }
    const { data, error } = await query.order('next_occurrence_date', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapRecurringPayment);
  }

  async getById(id: string): Promise<RecurringPayment | null> {
    const { data, error } = await this.supabase
      .from('recurring_payments')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapRecurringPayment(data);
  }

  async create(input: CreateRecurringPaymentInput): Promise<RecurringPayment> {
    const next = input.nextOccurrenceDate ?? input.startDate;
    const payload = {
      label: input.label,
      payment_type: input.paymentType,
      payment_method: input.paymentMethod,
      third_party_type: input.thirdPartyType,
      third_party_id: input.thirdPartyId,
      bank_account_id: input.bankAccountId ?? null,
      amount: input.amount,
      notes: input.notes ?? null,
      frequency: input.frequency,
      interval_count: input.intervalCount ?? 1,
      day_of_month: input.dayOfMonth ?? null,
      start_date: input.startDate,
      end_date: input.endDate ?? null,
      next_occurrence_date: next,
      is_active: input.isActive ?? true,
      auto_generate: input.autoGenerate ?? true,
    };

    const { data, error } = await this.supabase
      .from('recurring_payments')
      .insert(payload as never)
      .select()
      .single();
    if (error) throw error;
    return mapRecurringPayment(data);
  }

  async update(id: string, input: UpdateRecurringPaymentInput): Promise<RecurringPayment> {
    const patch: Record<string, unknown> = {};
    if (input.label !== undefined) patch['label'] = input.label;
    if (input.paymentType !== undefined) patch['payment_type'] = input.paymentType;
    if (input.paymentMethod !== undefined) patch['payment_method'] = input.paymentMethod;
    if (input.thirdPartyType !== undefined) patch['third_party_type'] = input.thirdPartyType;
    if (input.thirdPartyId !== undefined) patch['third_party_id'] = input.thirdPartyId;
    if (input.bankAccountId !== undefined) patch['bank_account_id'] = input.bankAccountId;
    if (input.amount !== undefined) patch['amount'] = input.amount;
    if (input.notes !== undefined) patch['notes'] = input.notes;
    if (input.frequency !== undefined) patch['frequency'] = input.frequency;
    if (input.intervalCount !== undefined) patch['interval_count'] = input.intervalCount;
    if (input.dayOfMonth !== undefined) patch['day_of_month'] = input.dayOfMonth;
    if (input.startDate !== undefined) patch['start_date'] = input.startDate;
    if (input.endDate !== undefined) patch['end_date'] = input.endDate;
    if (input.nextOccurrenceDate !== undefined)
      patch['next_occurrence_date'] = input.nextOccurrenceDate;
    if (input.lastGeneratedDate !== undefined)
      patch['last_generated_date'] = input.lastGeneratedDate;
    if (input.isActive !== undefined) patch['is_active'] = input.isActive;
    if (input.autoGenerate !== undefined) patch['auto_generate'] = input.autoGenerate;

    const { data, error } = await this.supabase
      .from('recurring_payments')
      .update(patch as never)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapRecurringPayment(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase
      .from('recurring_payments')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async generateDue(today?: string): Promise<GenerateDueResult> {
    const todayDate = today ?? todayISO();

    const rules = await this.list({ isActive: true });
    const dueRules: { rule: RecurringPayment; dueRule: DueRule }[] = rules
      .filter((r) => r.autoGenerate)
      .map((rule) => ({
        rule,
        dueRule: {
          id: rule.id,
          isActive: rule.isActive,
          autoGenerate: rule.autoGenerate,
          frequency: rule.frequency,
          interval: rule.intervalCount,
          dayOfMonth: rule.dayOfMonth,
          startDate: rule.startDate,
          endDate: rule.endDate,
          nextOccurrenceDate: rule.nextOccurrenceDate,
        },
      }));

    let generated = 0;

    for (const { rule, dueRule } of dueRules) {
      const result = generateOccurrencesForRule(dueRule, todayDate);
      if (result.occurrences.length === 0) continue;

      for (const occurrenceDate of result.occurrences) {
        const number = await this.getNextPaymentNumber(rule.paymentType);
        const insertPayload: Record<string, unknown> = {
          number,
          payment_type: rule.paymentType,
          payment_method: rule.paymentMethod,
          third_party_type: rule.thirdPartyType,
          third_party_id: rule.thirdPartyId,
          bank_account_id: rule.bankAccountId,
          amount: rule.amount,
          payment_date: occurrenceDate,
          notes: rule.notes ?? rule.label,
          recurring_payment_id: rule.id,
        };

        const { error: insertErr } = await this.supabase
          .from('payments')
          .insert(insertPayload as never);
        if (insertErr) throw insertErr;
        generated++;
      }

      await this.update(rule.id, {
        nextOccurrenceDate: result.newNextOccurrenceDate,
        lastGeneratedDate: result.lastGeneratedDate,
      });
    }

    return { generated, rulesProcessed: dueRules.length };
  }

  private async getNextPaymentNumber(type: PaymentType): Promise<string> {
    const prefix = PAYMENT_TYPE_PREFIX[type];
    const seqName = PAYMENT_TYPE_SEQUENCE[type];
    const year = new Date().getFullYear();

    const { data, error } = await this.supabase.rpc('get_next_sequence_value', {
      seq_name: seqName,
    });
    if (!error && data != null) {
      return `${prefix}-${year}-${String(data).padStart(4, '0')}`;
    }

    const { count } = await this.supabase
      .from('payments')
      .select('*', { count: 'exact', head: true })
      .eq('payment_type', type);
    const next = (count ?? 0) + 1;
    return `${prefix}-${year}-${String(next).padStart(4, '0')}`;
  }
}

function mapRecurringPayment(row: Record<string, unknown>): RecurringPayment {
  return {
    id: row['id'] as string,
    label: row['label'] as string,
    paymentType: row['payment_type'] as RecurringPayment['paymentType'],
    paymentMethod: row['payment_method'] as RecurringPayment['paymentMethod'],
    thirdPartyType: row['third_party_type'] as RecurringPayment['thirdPartyType'],
    thirdPartyId: row['third_party_id'] as string,
    bankAccountId: (row['bank_account_id'] as string) ?? null,
    amount: Number(row['amount']),
    notes: (row['notes'] as string) ?? null,
    frequency: row['frequency'] as RecurringPayment['frequency'],
    intervalCount: (row['interval_count'] as number) ?? 1,
    dayOfMonth: (row['day_of_month'] as number) ?? null,
    startDate: row['start_date'] as string,
    endDate: (row['end_date'] as string) ?? null,
    nextOccurrenceDate: row['next_occurrence_date'] as string,
    lastGeneratedDate: (row['last_generated_date'] as string) ?? null,
    isActive: (row['is_active'] as boolean) ?? true,
    autoGenerate: (row['auto_generate'] as boolean) ?? true,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
